import {type Camera, Group, OrthographicCamera, Scene, SRGBColorSpace, WebGLRenderer,} from 'three';
import type {StandardViewType} from '../view-orientation/standardViewTypes';
import {ViewOrientationMesh} from '../view-orientation/ViewOrientationMesh';
import {ViewportGizmoAxisIndicator} from './ViewportGizmoAxisIndicator';
import {ViewportGizmoLabelTextureFactory} from './ViewportGizmoLabelTextureFactory';
import {ViewportGizmoOuterControls} from './ViewportGizmoOuterControls';


/** 视口方向导航器渲染配置。 */
export interface ViewportGizmoRendererOptions {
  /** 挂载独立导航器画布的 Viewer 容器。 */
  readonly container: HTMLElement;
  /** 导航器 CSS 逻辑边长。 */
  readonly size?: number;
  /** 导航器距视口底部的像素距离，默认值为 `16`。 */
  readonly bottom?: number;
  /** 导航器距视口左侧的像素距离，默认值为 `16`。 */
  readonly left?: number;
  /** 导航器距视口顶部的兼容像素距离；提供后覆盖 `bottom`。 */
  readonly top?: number;
  /** 导航器距视口右侧的兼容像素距离；提供后覆盖 `left`。 */
  readonly right?: number;
  /** 独立渲染器使用的设备像素比。 */
  readonly pixelRatio?: number;
}

/**
 * 在 Viewer 左下角使用独立透明 WebGL 场景渲染方向导航器。
 *
 * 中央方向立方体与坐标轴同步主相机逆四元数，外围按钮保持屏幕方向固定。
 */
export class ViewportGizmoRenderer {
  /** 默认逻辑边长。 */
  private static readonly DEFAULT_SIZE = 128;

  /** 默认边距。 */
  private static readonly DEFAULT_OFFSET = 16;

  /** 内部正交相机半宽。 */
  private static readonly CAMERA_HALF_SIZE = 90;

  /** 中央方向立方体显示边长。 */
  private static readonly ORIENTATION_MESH_WORLD_SIZE = 48;

  /** 独立透明场景。 */
  private readonly scene = new Scene();

  /** 导航器专用正交相机。 */
  private readonly camera = new OrthographicCamera(
    -ViewportGizmoRenderer.CAMERA_HALF_SIZE,
    ViewportGizmoRenderer.CAMERA_HALF_SIZE,
    ViewportGizmoRenderer.CAMERA_HALF_SIZE,
    -ViewportGizmoRenderer.CAMERA_HALF_SIZE,
    -1000,
    1000,
  );

  /** 跟随主相机姿态的中央组。 */
  private readonly rotatingGroup = new Group();

  /** 中央 26 向方向立方体。 */
  public readonly orientationMesh = new ViewOrientationMesh();

  /** 固定在屏幕方向的外围控制按钮。 */
  public readonly outerControls = new ViewportGizmoOuterControls();

  /** 中文方向和 XYZ 标签纹理工厂。 */
  private readonly labelTextureFactory = new ViewportGizmoLabelTextureFactory();

  /** 跟随中央组旋转的坐标轴。 */
  private readonly axisIndicator = new ViewportGizmoAxisIndicator(
    this.labelTextureFactory,
  );

  /** 接收指针事件的导航器根元素。 */
  public readonly domElement: HTMLDivElement;

  /** 独立渲染画布。 */
  private readonly canvasElement: HTMLCanvasElement;

  /** 导航器宿主容器。 */
  private readonly container: HTMLElement;

  /** 宿主容器挂载前的 position 样式。 */
  private readonly originalContainerPosition: string;

  /** 是否由导航器修改了宿主容器定位方式。 */
  private changedContainerPosition = false;

  /** 首帧延迟创建的 WebGLRenderer。 */
  private renderer: WebGLRenderer | null = null;

  /** 当前环境确认无法创建 WebGL 上下文。 */
  private rendererUnavailable = false;

  /** 最终逻辑边长。 */
  private readonly size: number;

  /** 最终设备像素比。 */
  private readonly pixelRatio: number;

  /** 是否已经释放。 */
  private disposed = false;

  /**
   * 创建并挂载导航器 DOM 与 Three.js 场景对象。
   *
   * @param options 容器、尺寸、边距和像素比配置。
   * @throws {TypeError} 当配置的容器不是 HTMLElement 时抛出错误。
   */
  public constructor(options: ViewportGizmoRendererOptions) {
    if (!ViewportGizmoRenderer.isHTMLElement(options.container)) {
      throw new TypeError('视口方向导航器容器必须是 HTMLElement。');
    }

    this.container = options.container;
    this.originalContainerPosition = this.container.style.position;
    this.size = ViewportGizmoRenderer.resolvePositiveNumber(
      options.size,
      ViewportGizmoRenderer.DEFAULT_SIZE,
    );
    this.pixelRatio = Math.min(
      ViewportGizmoRenderer.resolvePositiveNumber(options.pixelRatio, 1),
      2,
    );
    this.domElement = this.container.ownerDocument.createElement('div');
    this.canvasElement = this.container.ownerDocument.createElement('canvas');

    this.configureDomElement(options);
    this.configureScene();
  }

  /** 获取射线拾取使用的内部正交相机。 */
  public get pickingCamera(): OrthographicCamera {
    return this.camera;
  }

  /**
   * 同步主相机姿态并渲染导航器。
   *
   * @param mainCamera Viewer 当前活动相机。
   * @param activeViewType 当前匹配的标准视图。
   */
  public render(
    mainCamera: Camera,
    activeViewType: StandardViewType | null,
  ): void {
    if (this.disposed) {
      return;
    }

    // 主相机四元数的逆旋转表达世界坐标轴在屏幕中的当前朝向。
    this.rotatingGroup.quaternion.copy(mainCamera.quaternion).invert();
    this.rotatingGroup.updateMatrixWorld(true);
    this.orientationMesh.setActiveViewType(activeViewType);

    const renderer = this.ensureRenderer();
    renderer?.render(this.scene, this.camera);
  }

  /** 释放 DOM、渲染器、几何、材质和纹理。 */
  public dispose(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.orientationMesh.dispose();
    this.outerControls.dispose();
    this.axisIndicator.dispose();
    this.labelTextureFactory.dispose();
    this.scene.clear();
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
    this.renderer = null;
    this.domElement.remove();

    // 仅在当前值仍是导航器写入值时恢复，避免覆盖宿主后续主动修改。
    if (this.changedContainerPosition && this.container.style.position === 'relative') {
      this.container.style.position = this.originalContainerPosition;
    }
    this.changedContainerPosition = false;
  }

  /**
   * 配置导航器 DOM 挂载和定位。
   *
   * @param options 导航器位置与尺寸配置。
   */
  private configureDomElement(options: ViewportGizmoRendererOptions): void {
    this.domElement.dataset.viewportGizmo = 'true';
    this.domElement.setAttribute('aria-label', '视口方向导航器');
    this.domElement.style.position = 'absolute';

    // 默认锚定左下角，同时保留顶部和右侧参数的兼容能力。
    if (options.top === undefined) {
      this.domElement.style.bottom = `${options.bottom ?? ViewportGizmoRenderer.DEFAULT_OFFSET}px`;
    } else {
      this.domElement.style.top = `${options.top}px`;
    }
    if (options.right === undefined) {
      this.domElement.style.left = `${options.left ?? ViewportGizmoRenderer.DEFAULT_OFFSET}px`;
    } else {
      this.domElement.style.right = `${options.right}px`;
    }
    this.domElement.style.width = `${this.size}px`;
    this.domElement.style.height = `${this.size}px`;
    this.domElement.style.zIndex = '180';
    this.domElement.style.touchAction = 'none';
    this.domElement.style.userSelect = 'none';
    this.canvasElement.style.display = 'block';
    this.canvasElement.style.width = '100%';
    this.canvasElement.style.height = '100%';
    this.domElement.appendChild(this.canvasElement);

    const containerWindow = this.container.ownerDocument.defaultView;
    if (containerWindow?.getComputedStyle(this.container).position === 'static') {
      this.container.style.position = 'relative';
      this.changedContainerPosition = true;
    }
    this.container.appendChild(this.domElement);
  }

  /** 装配中央旋转组、固定外部组和内部相机。 */
  private configureScene(): void {
    this.camera.position.set(0, 0, 170);
    this.camera.layers.enable(ViewOrientationMesh.MESH_LAYER);
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld(true);

    this.orientationMesh.visible = true;
    this.orientationMesh.scale.setScalar(
      ViewportGizmoRenderer.ORIENTATION_MESH_WORLD_SIZE
        / ViewOrientationMesh.DEFAULT_SIZE,
    );
    this.labelTextureFactory.applyFaceLabels(this.orientationMesh);
    this.rotatingGroup.add(this.orientationMesh, this.axisIndicator);
    this.scene.add(this.rotatingGroup, this.outerControls);
    this.scene.updateMatrixWorld(true);
  }

  /**
   * 首帧创建并配置透明 WebGLRenderer。
   *
   * @returns 可用渲染器；无 WebGL 环境返回 `null`。
   */
  private ensureRenderer(): WebGLRenderer | null {
    if (this.renderer || this.rendererUnavailable) {
      return this.renderer;
    }

    // 测试、服务端渲染或禁用 WebGL 的环境保留 DOM 与业务事件能力。
    const ownerWindow = this.container.ownerDocument.defaultView;
    if (
      !ownerWindow
      || (
        !('WebGLRenderingContext' in ownerWindow)
        && !('WebGL2RenderingContext' in ownerWindow)
      )
    ) {
      this.rendererUnavailable = true;
      return null;
    }

    try {
      const renderer = new WebGLRenderer({
        canvas: this.canvasElement,
        antialias: true,
        alpha: true,
      });
      renderer.outputColorSpace = SRGBColorSpace;
      renderer.setClearColor(0x000000, 0);
      renderer.setPixelRatio(this.pixelRatio);
      renderer.setSize(this.size, this.size, false);
      this.renderer = renderer;
    } catch (error) {
      // 浏览器拒绝创建上下文时禁用绘制，避免每帧重复抛出相同错误。
      this.rendererUnavailable = true;
      console.warn(
        '[ViewportGizmoRenderer] 无法创建独立 WebGLRenderer，已保留交互并禁用绘制。',
        error,
      );
    }

    return this.renderer;
  }

  /**
   * 判断未知对象是否为其所属文档中的 HTMLElement。
   *
   * @param value 待检查对象。
   * @returns 属于 HTMLElement 时返回 `true`。
   */
  private static isHTMLElement(value: unknown): value is HTMLElement {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as { ownerDocument?: Document };
    const htmlElementConstructor = candidate.ownerDocument?.defaultView?.HTMLElement;
    return Boolean(htmlElementConstructor && value instanceof htmlElementConstructor);
  }

  /**
   * 解析正有限数配置。
   *
   * @param value 用户输入值。
   * @param fallback 无效输入的默认值。
   * @returns 可安全用于尺寸或像素比的正有限数。
   */
  private static resolvePositiveNumber(
    value: number | undefined,
    fallback: number,
  ): number {
    return value !== undefined && Number.isFinite(value) && value > 0
      ? value
      : fallback;
  }
}

